import { EProductImageType } from '@/utils/enums/goods';
import type { BestsellerProduct, BestsellerVibe } from './types';
import {
  hasBestsellerProductAvailability,
  isBestsellerProductUnavailable,
} from './bestsellerAvailability';

export const VIBE_PAGE_SIZE = 4;
export const VIBE_LOAD_BATCH_SIZE = VIBE_PAGE_SIZE * 2;
export const VIBE_AUTOPLAY_MS = 5000;
export const VIBE_TRANSITION_MS = 500;
export const VIBE_HOVER_IMAGE_RULE = [
  EProductImageType.MATERIAL,
  EProductImageType.CELEBRITY,
  EProductImageType.PRODUCT,
];

export type AvailableBestsellerVibe = BestsellerVibe & { products: BestsellerProduct[] };

function isAvailabilityRecordAvailable(record: Record<string, unknown>): boolean {
  return !hasBestsellerProductAvailability(record) || !isBestsellerProductUnavailable(record);
}

export function isBestsellerProductAvailable(product: BestsellerProduct): boolean {
  const productRecord = product as unknown as Record<string, unknown>;
  if (
    hasBestsellerProductAvailability(productRecord) &&
    isAvailabilityRecordAvailable(productRecord)
  ) {
    return true;
  }

  const correlation = product.correlation || [];
  if (correlation.length) {
    return correlation.some(isAvailabilityRecordAvailable);
  }

  return isAvailabilityRecordAvailable(productRecord);
}

export function resolveAvailableVibes(
  vibes: BestsellerVibe[],
  legacyProducts: BestsellerProduct[],
): AvailableBestsellerVibe[] {
  return vibes.flatMap((vibe, index) => {
    const resolvedProducts =
      vibe.products === undefined && index === 0 ? legacyProducts : vibe.products || [];

    return resolvedProducts.some(isBestsellerProductAvailable)
      ? [{ ...vibe, products: resolvedProducts }]
      : [];
  });
}

export function getVibePageProducts(
  products: BestsellerProduct[],
  page: number,
): BestsellerProduct[] {
  const start = page * VIBE_PAGE_SIZE;
  return products.slice(start, start + VIBE_PAGE_SIZE);
}

export function shouldLoadMoreVibeProducts(
  currentPage: number,
  loadedPageCount: number,
  hasMore: boolean,
  loading: boolean,
): boolean {
  return (
    hasMore &&
    !loading &&
    (loadedPageCount === 0 ? currentPage === 0 : currentPage >= loadedPageCount - 1)
  );
}

export function mergeVibeProducts(
  current: BestsellerProduct[],
  incoming: BestsellerProduct[],
): BestsellerProduct[] {
  const existingKeys = new Set(current.map((product) => product.shopSku || String(product.id)));
  return current.concat(
    incoming.filter((product) => !existingKeys.has(product.shopSku || String(product.id))),
  );
}

export function moveVibePage(current: number, pageCount: number, direction: -1 | 1): number {
  if (pageCount <= 1) return 0;
  return (current + direction + pageCount) % pageCount;
}
